import type { UploadCategory } from './fileTypes';

/**
 * Enough of each category's old extension list to recognise a stored upload
 * field configured for it, before categories were persisted directly.
 *
 * Only a few extensions per category are needed, and only ones that existed
 * when those lists were written — a newer extension could never appear in a
 * stored array, and matching against the live table would let today's additions
 * change how an existing field is read.
 *
 * `.pdf` sits alongside `.doc` because a field can be configured for documents
 * and only ever have had PDF in its list; production holds such a row, and
 * `.doc` alone would silently downgrade it to images-only.
 */
const LEGACY_CATEGORY_SENTINELS: ReadonlyArray<
  [UploadCategory, readonly string[]]
> = [
  ['image', ['.jpg']],
  ['document', ['.doc', '.pdf']],
  ['video', ['.mp4']],
];

/**
 * Recover the categories an upload field was configured with, from the list of
 * accepted extensions it was stored with.
 */
export const categoriesFromLegacyExtensions = (
  extensions: readonly string[]
): UploadCategory[] => {
  const stored = new Set(extensions.map((e) => e.toLowerCase()));
  return LEGACY_CATEGORY_SENTINELS.filter(([, sentinels]) =>
    sentinels.some((extension) => stored.has(extension))
  ).map(([category]) => category);
};
